import * as AsyncLock from "async-lock";
import axios, {
    AxiosRequestConfig,
    AxiosResponse,
    CancelTokenStatic,
    CancelTokenSource
} from "axios";

import { AuthVerified } from "./../models/generic/auth-transfer";
import {
    APICONFIG_HEADER_AUTHORIZATION,
    LOCALSTORAGE_USERSSION,
    // APICONFIG_HEADER_LANGUAGE,
    LOCALSTORAGE_AUTH2,
    APICONFIG_HEADER_SESSIONID,
    WEBAPP_LAYOUT,
    SERVICE_LINKS,
    LOCALSTORAGE_AUTH,
    EVENT_BUS
} from "./../common/constants";
import EventBus from "./event-bus";

import { ApiResponse } from "../models/app-configuration";
import serverErrorCodeConstant from "../common/server-error-code.constant";
import VueI18n from "vue-i18n";
import {
    Oauthorization2Response, Oauth2Data,
} from "./authorization/data-model";
import {
    LocalStoredOAuthorization2Response
} from "./authorization/authorizationFactory";
import { LocalforageWrapper } from "./localforage-wrapper";
import { captureAppError, markCaptured } from "./error-reporter";
import { classifyRefreshResponse, createSharedRefresh, RefreshResult } from "./token-refresh";
import { isWinAppWebview } from "./authorization/webview-access-token";

import store from "../store";


export interface IAxiosConfigurationHelper {
    axiosRequestHandler(config: AxiosRequestConfig, i18n: VueI18n);
    axiosResponseHandler(config: AxiosResponse);
    axiosValidResponseHandler(config: AxiosRequestConfig);
    axiosInternalExceptionResponseHandler(config: AxiosResponse);
    axiosNotFoundResponseHandler(config: AxiosResponse);
    axiosUnauthorizedResponseHandler(config: AxiosRequestConfig);
    axiosExceptionHandler(error: any, a);
}

let helperInstance: AxiosConfigurationHelper = null;

export const axiosConfigurationHelper = () => helperInstance;
export class AxiosConfigurationHelper implements IAxiosConfigurationHelper {
    refreshTokenLocker = new AsyncLock({
        maxPending: 100 * 100 * 100
    });
    refreshTokenBusy = false;
    dtCancelToken: CancelTokenStatic;
    dtCancelTokenSource: CancelTokenSource;
    numberOfFailure: number;
    serviceAvailable: boolean | null = null;
    serviceUnavailable: number;
    sessionExpired: number;

    //counter = 0;

    constructor() {
        this.numberOfFailure = 0;
        this.dtCancelToken = axios.CancelToken;
        this.dtCancelTokenSource = this.dtCancelToken.source();
        this.serviceUnavailable = 0;
        this.sessionExpired = 0;
        // main.ts builds the one instance that owns the token-refresh machinery;
        // code outside the interceptors reaches it through axiosConfigurationHelper().
        helperInstance = this;
    }
    axiosHandleValidStatus(status: number) {
        return status >= 200 && status <= 504;
    }

    private clearAuthDataStorage() {
        localStorage.removeItem("auth");
        localStorage.removeItem("AuthorizationData");
        localStorage.removeItem("userssion");
        LocalforageWrapper.clear();
    }

    private sessionEnded = false;

    // A dead refresh token: tell the embedded apps, then drop the session. Once per page:
    // concurrent 401s that share one failed refresh all land here.
    private endSession(): void {
        if (this.sessionEnded) return;
        this.sessionEnded = true;
        EventBus.$emit(EVENT_BUS.AUTH.SIGNED_OUT, "session-expired");
        this.clearAuthDataStorage();
        this.gotoLogin();
    }

    private gotoLogin() {
        console.log(store)
        const webappLayout = store.state.common.webappLayoutMode;
        if (webappLayout === WEBAPP_LAYOUT.DEFAULT) {
            const authSettings = JSON.parse(
                localStorage.getItem("AuthorizationSettingData")
            );

            const loginUrlEl = document.querySelector(
                "meta[name='Login-Url']"
            );

            if (loginUrlEl) {
                location.href = loginUrlEl.getAttribute("content");
            } else {
                location.href = authSettings.LoginUrl;
            }
        }


    }
    // Single choke point: every server error response (>= 400) is reported
    // to Sentry here, regardless of how (or whether) the calling code handles
    // the rejection later. The response body is marked as captured so
    // downstream handlers (toasts, save-error handlers) do not report it again.
    private reportServerError(response: AxiosResponse): void {
        const status = response.status;
        if (!status || status < 400) return;
        // 401 is the token-refresh flow: the request is retried transparently.
        if (status === 401) return;
        // A caller that treats a status as a normal answer opts out per request,
        // e.g. brochure/checking-key answers 404 when the office has no brochure key.
        const config = response.config as AxiosRequestConfig & { expectedStatuses?: number[] };
        if (config?.expectedStatuses?.includes(status)) return;

        const body: any = response.data || {};
        const apiCode = body.code !== undefined ? body.code : body.data?.code;
        // Server-side validation is an expected user error.
        if (
            apiCode === serverErrorCodeConstant.ValidationException ||
            body.data?.code === serverErrorCodeConstant.ValidationException
        ) {
            return;
        }
        // Concurrency conflicts open the conflict modal; they are an expected
        // outcome, recorded as a Sentry breadcrumb in the
        // common/setVisibleConflictDataInfo mutation, not as an error.
        // property/project 20506, person 21013, task 22004, agenda 23010,
        // demand 24005, company 30502.
        const conflictCodes = [20506, 21013, 22004, 23010, 24005, 30502];
        if (conflictCodes.includes(apiCode) || conflictCodes.includes(body.data?.code)) {
            return;
        }

        const message = body.message || body.msg || `HTTP ${status}`;
        const error = new Error(
            typeof message === "string" ? message : JSON.stringify(message)
        );
        error.name = "ApiError";

        captureAppError(error, {
            tags: {
                origin: "api",
                status: String(status),
                ...(apiCode !== undefined ? { apiCode: String(apiCode) } : {})
            },
            extra: {
                url: response.config?.url,
                method: response.config?.method,
                requestBody: typeof response.config?.data === "string"
                    ? response.config.data.slice(0, 2000)
                    : undefined
            }
        });
        // The body object is what services reject with downstream.
        markCaptured(body);
    }

    axiosResponseHandler(response: AxiosResponse) {
        if (this.serviceAvailable === null) this.serviceAvailable = true;

        // Skip all interceptor handling if skipRetry flag is set - return server response directly
        const requestConfig = response.config as any;
        if (requestConfig?.skipRetry && response.status >= 400) {
            // Keep original server response data (including errors array)
            response.data = new ApiResponse({
                isSuccess: false,
                data: response.data,  // Contains { errors: [{ Code: "..." }] }
                code: response.status,
                message: response.statusText
            });
            this.reportServerError(response);
            return response;
        }

        switch (response.status) {
            case 200:
            case 201:
            case 204:
                //this.counter++;
                // Idempotent: a retried request (token-refresh flow) can run this
                // handler over an already-wrapped response â€” don't wrap twice.
                if (!(response.data instanceof ApiResponse)) {
                    response.data = new ApiResponse(
                        { isSuccess: true, data: response.data },
                        response.headers
                    );
                }
                break;
            //if (this.counter < 30) break;
            case 400:
                if (response.data.code == serverErrorCodeConstant.ValidationException) {
                    response.data = new ApiResponse({
                        isSuccess: false,
                        data: response.data,
                        code: response.status,
                        message: response.statusText
                    });
                }
                break;
            case 401:
                const originalRequest = response.config as any;
                const userSession: AuthVerified = JSON.parse(localStorage.getItem(LOCALSTORAGE_USERSSION) || "null");
                // OMSEVEN-10852: inside the WinApp only the WinApp refreshes and pushes the new token.
                if (!userSession?.RefreshToken || isWinAppWebview()) return;

                return this.sharedRefresh().then(result => {
                    if (result.outcome === "refreshed") {
                        originalRequest.headers["Authorization"] = "Bearer " + result.token;
                        return axios(originalRequest);
                    }
                    // The interceptor keeps ending the session on any failed refresh: a temporary failure here would
                    // otherwise retry into another 401 with no cap. "session-ended" already did this in performRefresh.
                    if (result.outcome === "temporary-failure") this.endSession();
                    return new Promise<AxiosResponse>(() => undefined); // the caller never resolves while the page leaves
                });

            case 404:
                response.data = new ApiResponse({
                    isSuccess: false,
                    data: null,
                    code: response.status,
                    message: response.statusText
                });
                break;
            case 500:
                response.data = new ApiResponse({
                    isSuccess: false,
                    data: response.data,
                    code: response.data.code, //1000
                    message: response.data.msg //
                });
                break;
            case 501:
            case 502:
            case 503:
                this.serviceUnavailable++;
                break;
            default:
                break;
        }
        this.reportServerError(response);
        return response;
    }

    axiosExceptionHandler(error: any) {
        if (error.config.url.indexOf("/files/upload") >= 0) {
            //todo if failed upload Picture,Document
        } else {
            this.serviceUnavailable++;
            if (this.serviceAvailable === null) this.serviceAvailable = true;
            return Promise.reject(error);
        }
    }
    axiosRequestHandler(config: AxiosRequestConfig, i18n: VueI18n) {
        // get auth2 from local store
        // update to request config
        let auth2 = new LocalStoredOAuthorization2Response();
        let auth2Data = auth2.authorizationData as Oauth2Data;
        if (auth2Data) {
            // axios 1.x: per-request `config.headers` is an `AxiosHeaders` instance
            // with no `.common` namespace (`.common` only exists on `axios.defaults.headers`).
            // Set the per-request Authorization header directly.
            config.headers[APICONFIG_HEADER_AUTHORIZATION] =
                "Bearer " + auth2Data.oauth2.accessToken;
        }
        return this.refreshTokenLocker.acquire(
            AxiosConfigurationHelper.name,
            async done => {
                if (this.numberOfFailure !== 0) {
                    config.cancelToken = this.dtCancelTokenSource.token;
                    this.dtCancelTokenSource.cancel("Sessions expired");
                    this.numberOfFailure = 0;
                }
                done(null, config);
            }
        );
    }
    axiosValidResponseHandler(response: AxiosResponse) {
        return response;
    }
    axiosInternalExceptionResponseHandler(response: AxiosResponse) {
        return response;
    }
    axiosNotFoundResponseHandler(response: AxiosResponse) {
        return response;
    }
    axiosUnauthorizedResponseHandler(response: AxiosResponse): ApiResponse {
        return new ApiResponse({
            isSuccess: false,
            code: response.data.Code,
            message: response.data.Details
        });
    }

    refreshAccessToken(): Promise<any> {
        // const currentAuthorizationConfig = localStorage.getItem(LOCALSTORAGE_AUTH);
        const currentUserSession = localStorage.getItem(LOCALSTORAGE_USERSSION);
        const userSession: AuthVerified = JSON.parse(currentUserSession);
        // const config: AuthTransfer = JSON.parse(currentAuthorizationConfig);
        const instance = axios.create({
            //baseURL: config.authApiEndPoint,
            baseURL: SERVICE_LINKS.AUTH_URL,
            headers: {
                [APICONFIG_HEADER_AUTHORIZATION]: "Bearer " + userSession.Token,
                [APICONFIG_HEADER_SESSIONID]: userSession.SessionId || ''
            }
        });
        console.log("start refresh token:" + JSON.stringify(userSession));
        return instance.get<{ [key: string]: any }>(
            `/auth/web/refresh/${userSession.RefreshToken}`
        );
    }

    // Single place where an axios refresh becomes the app's authState: STATE_CHANGED
    // lets the embedded-app broker push the new token to every active iframe.
    private persistRefreshedToken(newToken: AxiosResponse, userSession: AuthVerified): string {
        // UPDATE CURRENT REQUEST
        axios.defaults.headers.common[APICONFIG_HEADER_AUTHORIZATION] = "Bearer " + newToken.data.AccessToken;
        //todo (huyngo): will refresh SessionId if improve reset value when refresh-token
        //axios.defaults.headers.common[APICONFIG_HEADER_SESSIONID] = newToken.data.Data.SessionId;

        const now = new Date();
        newToken['accessTokenExpiration'] = new Date(now.getTime() + ((parseInt((newToken as any).data.expiresIn) - 60) * 1000));
        newToken.data['accessTokenExpiration'] = new Date(now.getTime() + ((parseInt((newToken as any).data.expiresIn) - 60) * 1000));
        newToken.data.data['accessTokenExpiration'] = new Date(now.getTime() + ((parseInt((newToken as any).data.expiresIn) - 60) * 1000));

        // STORED USER SESSION
        userSession.Token = newToken.data.accessToken;
        userSession.RefreshToken = newToken.data.refreshToken;

        //todo (huyngo): will refresh SessionId if improve reset value when refresh-token
        //userSession.SessionId = newToken.data.Data.SessionId;

        localStorage.setItem(LOCALSTORAGE_USERSSION, JSON.stringify(userSession));

        // STORED OAUTH2
        // NOTE: REFRESH TOKEN ONLY SUPPORT FOR OAUTH2
        const auth2Json = localStorage.getItem(LOCALSTORAGE_AUTH2);
        if (auth2Json != null) {
            let auth2: Oauthorization2Response = JSON.parse(auth2Json);
            auth2.oauth2 = newToken.data as any;
            auth2.data.oauth2 = newToken.data as any;
            localStorage.setItem(
                LOCALSTORAGE_AUTH2,
                JSON.stringify(auth2)
            );
        }

        const authJson = localStorage.getItem(LOCALSTORAGE_AUTH);
        if (authJson != null) {
            let auth: any = JSON.parse(authJson);
            auth.token = newToken.data.accessToken;
            localStorage.setItem(LOCALSTORAGE_AUTH, JSON.stringify(auth));
        }

        EventBus.$emit(EVENT_BUS.AUTH.STATE_CHANGED);

        return newToken.data.accessToken;
    }

    private readonly sharedRefresh = createSharedRefresh(() => this.performRefresh());

    private async performRefresh(): Promise<RefreshResult> {
        const userSession: AuthVerified = JSON.parse(localStorage.getItem(LOCALSTORAGE_USERSSION) || "null");
        if (!userSession?.RefreshToken || isWinAppWebview()) return { outcome: "temporary-failure", token: null };
        // A network error rejects; createSharedRefresh maps it to temporary-failure.
        const response: AxiosResponse = await this.refreshAccessToken();
        const outcome = classifyRefreshResponse(response.status, response.data);
        if (outcome === "refreshed") return { outcome, token: this.persistRefreshedToken(response, userSession) };
        if (outcome === "session-ended") this.endSession();
        return { outcome, token: null };
    }

    // Shared token refresh for the embedded-app broker. Joins a refresh already in flight.
    // Resolves the new token, or null when it failed. A dead refresh token (400/401/error body)
    // ends the session; network errors and other statuses do not.
    ensureFreshAccessToken(): Promise<string | null> {
        return this.sharedRefresh().then(result => result.token);
    }
}
